/**
 * Shopper-facing USD display helpers (CP-136 / CP-74).
 *
 * One format sitewide: $1,249 (thousands separators, whole dollars by default).
 * Default rounding is Math.round to whole dollars so Detail Current / Lowest /
 * Highest tiles and the From/price card agree for the same cabin (CP-74:
 * 1744.5 → $1,745 — never a bare toLocaleString half-dollar like $1,744.5).
 * Pass { digits: N } when cents (or other fraction digits) are intentional.
 * Display-only — do not change underlying price values.
 */

/**
 * Amount without currency symbol. 1249 → "1,249".
 * @param {*} value
 * @param {{ digits?: number, fallback?: string }} [options]
 */
export function formatPriceAmount(value, options = {}) {
  const { digits, fallback = '0' } = options;
  if (value == null || value === '') return fallback;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return fallback;

  if (digits == null) {
    // CP-74: one shopper-facing rule — half-dollars round half-up (Math.round).
    return Math.round(amount).toLocaleString('en-US', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    });
  }

  return amount.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
}

/**
 * Full shopper price including $. 1249 → "$1,249".
 * @param {*} value
 * @param {{ digits?: number, fallback?: string }} [options]
 */
export function formatPrice(value, options = {}) {
  const { fallback = '$0', ...rest } = options;
  if (value == null || value === '') return fallback;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return fallback;
  return `$${formatPriceAmount(amount, rest)}`;
}

/**
 * Compare / chart money with fixed fraction digits (includes $).
 * Defaults to 2 decimal places for average/compare values.
 */
export function formatCompareMoney(value, digits = 2) {
  if (value == null || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
}
